import React from "react";
import { Link } from "react-router-dom";
import useSEO from "../lib/useSEO";
import { ArrowRight, DollarSign, Repeat, Shield, Users, Link as LinkIcon, Check, Info } from "lucide-react";

const COMMISSIONS = [
    { tier: "Basic", amount: 5, note: "Per referred attorney, per month" },
    { tier: "Pro", amount: 15, note: "Per referred attorney, per month", emphasis: true },
    { tier: "Elite", amount: 30, note: "Per referred attorney, per month", emphasis: true },
];

const STEPS = [
    { n: 1, title: "Apply", text: "Two-minute application. We approve quickly and email you your unique referral code and link." },
    { n: 2, title: "Share", text: "Send your link (or code) to attorneys. They sign up at locallawfinder.com/join?ref=YOURCODE." },
    { n: 3, title: "Earn", text: "When your referred attorney pays for Basic, Pro, or Elite — you earn recurring monthly commissions for as long as they stay subscribed." },
    { n: 4, title: "Get Paid", text: "Pending commissions become approved after a 30-day hold (protects against refunds). Payouts are currently sent manually by LocalLawFinder on a defined schedule while Stripe Connect verification is being completed. Once verified, payouts will move to automated Stripe transfers." },
];

export default function ForAffiliatesPage() {
    useSEO({
        title: "Referral Partner Program — Earn Recurring Commissions",
        description:
            "Help attorneys find LocalLawFinder and earn $5–$30/mo per referral for as long as they stay subscribed. Secure Stripe payouts. Built for community organizers, legal professionals, and consultants.",
        path: "/for-affiliates",
    });

    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="text-center mb-12">
                <span className="ll-pill mb-3">Referral Partners</span>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="affiliate-hero">
                    Refer attorneys.<br />
                    <span className="text-primary">Earn every month they stay.</span>
                </h1>
                <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                    A clean, recurring referral program for legal professionals, community organizers, consultants,
                    and anyone with a network of attorneys looking for visibility.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                    <Link to="/affiliate/auth?mode=register"
                          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-semibold hover:bg-primary/90 transition-colors"
                          data-testid="affiliate-apply-cta">
                        Apply to join <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link to="/affiliate/auth?mode=login"
                          className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                          data-testid="affiliate-login-link">
                        Already a partner? Sign in
                    </Link>
                </div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-5 sm:p-6 mb-10" data-testid="affiliate-payout-status-banner">
                <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <h2 className="font-serif text-lg font-semibold mb-1">Affiliate payouts (current status)</h2>
                        <p className="text-sm text-foreground/80 leading-relaxed mb-3">
                            Affiliate referrals are being tracked, but <strong>payouts are currently handled manually</strong> by LocalLawFinder
                            while Stripe Connect verification is being completed. Stripe-hosted onboarding is configured, but Stripe
                            requires identity and verification information before enabling automatic payouts to connected accounts.
                            This is temporary — once verification is satisfied, payouts will move to automated Stripe transfers through Connect.
                        </p>
                        <div className="text-sm text-foreground/80">
                            <div className="font-semibold mb-1">How the affiliate program works right now:</div>
                            <ul className="space-y-1 list-disc pl-5 marker:text-primary">
                                <li>Each affiliate receives a personal referral link and code.</li>
                                <li>When an attorney signs up through that referral, the referral is recorded in the system.</li>
                                <li>Qualifying commissions are calculated based on the current referral offer.</li>
                                <li>Payouts are sent manually by LocalLawFinder on the payout schedule we define.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-14">
                {COMMISSIONS.map((c) => (
                    <div key={c.tier} className={`ll-card ${c.emphasis ? "ring-2 ring-primary" : ""}`}>
                        <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="h-5 w-5 text-primary" />
                            <h3 className="font-serif text-xl font-bold">{c.tier}</h3>
                        </div>
                        <div className="text-4xl font-bold tabular-nums mb-1">
                            ${c.amount}<span className="text-base text-muted-foreground font-normal">/mo</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{c.note}</p>
                    </div>
                ))}
            </div>

            <div className="mb-14">
                <h2 className="font-serif text-2xl font-bold mb-6">How it works</h2>
                <div className="grid md:grid-cols-2 gap-5">
                    {STEPS.map((s) => (
                        <div key={s.n} className="ll-card">
                            <div className="flex items-start gap-3">
                                <span className="bg-primary text-primary-foreground font-bold text-sm rounded-full h-7 w-7 flex items-center justify-center shrink-0">{s.n}</span>
                                <div>
                                    <h3 className="font-serif text-lg font-semibold mb-1">{s.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5 mb-14">
                <div className="ll-card">
                    <Repeat className="h-5 w-5 text-primary mb-2" />
                    <h3 className="font-serif font-semibold mb-1">Recurring, not one-time</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">As long as your referred attorney stays subscribed, you keep earning every month.</p>
                </div>
                <div className="ll-card">
                    <Shield className="h-5 w-5 text-primary mb-2" />
                    <h3 className="font-serif font-semibold mb-1">Secure payouts (Stripe-ready)</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">Payouts are currently issued manually while Stripe Connect verification is being finalized. Stripe-hosted onboarding is already configured — once verification is complete, payouts auto-route through Stripe Connect (1099s handled by Stripe; your bank info never touches our database).</p>
                </div>
                <div className="ll-card">
                    <LinkIcon className="h-5 w-5 text-primary mb-2" />
                    <h3 className="font-serif font-semibold mb-1">First-touch attribution</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">Whoever brings the attorney first owns that relationship permanently. No stealing referrals.</p>
                </div>
            </div>

            <div className="rounded-lg bg-accent/30 border border-border p-6 sm:p-8 mb-12">
                <h3 className="font-serif text-xl font-bold mb-3">Commission lifecycle</h3>
                <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> <span><strong>Pending</strong> — created instantly when your attorney's payment lands. Sits for 30 days.</span></li>
                    <li className="flex items-start gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> <span><strong>Approved</strong> — after 30-day hold (protects against refunds, chargebacks, fake signups).</span></li>
                    <li className="flex items-start gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> <span><strong>Paid</strong> — currently sent manually by LocalLawFinder on the defined payout schedule. Once Stripe Connect verification is completed, this will switch to automated Stripe payouts.</span></li>
                    <li className="flex items-start gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> <span><strong>Reversed</strong> — if the attorney cancels or refunds during the hold window.</span></li>
                </ul>
            </div>

            <div className="rounded-lg bg-card border border-border p-6 sm:p-8 mb-12" data-testid="affiliate-faq">
                <h3 className="font-serif text-xl font-bold mb-4">FAQ</h3>
                <div className="space-y-5 text-sm">
                    <div>
                        <div className="font-semibold mb-1">Are payouts automatic right now?</div>
                        <p className="text-muted-foreground leading-relaxed">
                            No. At the moment, payouts are <strong>manual</strong> while Stripe Connect verification is still being completed.
                            Referral tracking can still operate now, and automatic payouts will be enabled later once Stripe verification
                            requirements are satisfied.
                        </p>
                    </div>
                    <div>
                        <div className="font-semibold mb-1">Should I still complete Stripe onboarding now?</div>
                        <p className="text-muted-foreground leading-relaxed">
                            Yes — completing Stripe-hosted onboarding now means your account is ready the moment verification finishes,
                            and you'll be migrated to automatic Stripe payouts without any extra step on your end.
                        </p>
                    </div>
                    <div>
                        <div className="font-semibold mb-1">What does manual payout actually look like?</div>
                        <p className="text-muted-foreground leading-relaxed">
                            LocalLawFinder reviews approved commissions on our defined payout schedule and sends payment using
                            whichever method we coordinate with you directly (we'll reach out before your first payout). All
                            referral activity, commission amounts, and statuses remain visible in your affiliate dashboard.
                        </p>
                    </div>
                </div>
            </div>

            <div className="text-center">
                <Link to="/affiliate/auth?mode=register"
                      className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-semibold hover:bg-primary/90 transition-colors"
                      data-testid="affiliate-apply-cta-bottom">
                    Start earning today <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </section>
    );
}
